import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button, buttonVariants } from '@/components/ui/button';
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';

import { ApiError } from '../../api/http';
import { registrationApi } from '../../api/waitlist';
import { useAuthStore } from '../../stores/useAuthStore';

/** 密碼長度下限跟後端 DTO 對齊，讓使用者在送出前就知道。 */
const MIN_PASSWORD_LENGTH = 12;

export function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { status, register } = useAuthStore(
    useShallow((state) => ({ status: state.status, register: state.register })),
  );

  const [form, setForm] = useState({ email: '', password: '', name: '', workspaceName: '' });
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  /**
   * 封測期間公開註冊是關的。先問一次狀態，關著就直接顯示「邀請制」，
   * 不要讓人填完四個欄位才被 403 打回來。
   *
   * 邀請連結長這樣：`/register?code=XXXX-XXXX-XXXX`——碼有效的話後端就回 open，
   * 表單照常出現，送出時再把碼一起帶上去核銷。
   *
   * 問不到（API 掛了）就照常顯示表單——真正的門在後端，前端只是別讓人白填。
   */
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('code') ?? undefined;
  const registration = useQuery({
    queryKey: ['registration-status', inviteCode ?? ''],
    queryFn: () => registrationApi.status(inviteCode),
    retry: false,
  });

  if (status === 'authenticated') {
    return <Navigate to="/" replace />;
  }

  if (registration.data?.open === false) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-primary/5 p-6">
        <div className="w-full max-w-[420px] rounded-xl bg-card p-6 shadow-card ring-1 ring-foreground/10">
          <h1 className="mb-1 text-2xl font-bold tracking-tight">
            {t('auth_register_closed_title')}
          </h1>
          <p className="mb-4 text-sm text-muted-foreground">{t('auth_register_closed_body')}</p>
          <a href="/#waitlist" className={buttonVariants({ className: 'w-full' })}>
            {t('auth_register_closed_cta')}
          </a>
          <p className="mt-4 text-sm text-muted-foreground">
            {t('auth_have_account')}{' '}
            <Link to="/login" className="text-primary underline underline-offset-4">
              {t('auth_go_login')}
            </Link>
          </p>
        </div>
      </main>
    );
  }

  /**
   * 先把值取出來再進 setState 的 updater。
   *
   * React 在 handler 回傳之後就把 event.currentTarget 清成 null，而 updater 是稍後
   * 才跑的——在裡面讀 currentTarget 會拿到 null。
   */
  const update = (key: keyof typeof form) => (event: { currentTarget: { value: string } }) => {
    const { value } = event.currentTarget;
    setForm((current) => ({ ...current, [key]: value }));
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setPending(true);

    try {
      await register(inviteCode === undefined ? form : { ...form, inviteCode });
      void navigate('/', { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('auth_error_unknown'));
    } finally {
      setPending(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-primary/5 p-6">
      <form
        className="w-full max-w-[420px] rounded-xl bg-card p-6 shadow-card ring-1 ring-foreground/10"
        onSubmit={(event) => void onSubmit(event)}
      >
        <h1 className="mb-1 text-2xl font-bold tracking-tight">{t('auth_register_title')}</h1>
        <p className="mb-4 text-sm text-muted-foreground">{t('auth_register_subtitle')}</p>

        <div className="flex flex-col gap-3">
          {error !== null && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Field>
            <FieldLabel htmlFor="register-name">{t('auth_name')}</FieldLabel>
            <Input
              id="register-name"
              name="name"
              autoComplete="name"
              required
              value={form.name}
              onChange={update('name')}
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="register-workspace">{t('auth_workspace_name')}</FieldLabel>
            <Input
              id="register-workspace"
              name="workspaceName"
              required
              value={form.workspaceName}
              onChange={update('workspaceName')}
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="register-email">{t('auth_email')}</FieldLabel>
            <Input
              id="register-email"
              type="email"
              name="email"
              autoComplete="email"
              required
              value={form.email}
              onChange={update('email')}
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="register-password">{t('auth_password')}</FieldLabel>
            <Input
              id="register-password"
              type="password"
              name="password"
              autoComplete="new-password"
              required
              minLength={MIN_PASSWORD_LENGTH}
              value={form.password}
              onChange={update('password')}
            />
            <FieldDescription>
              {t('auth_password_hint', { count: MIN_PASSWORD_LENGTH })}
            </FieldDescription>
          </Field>

          <Button type="submit" disabled={pending}>
            {pending && <Spinner data-icon="inline-start" />}
            {t('auth_register_submit')}
          </Button>
        </div>

        <p className="mt-4 text-sm text-muted-foreground">
          {t('auth_have_account')}{' '}
          <Link to="/login" className="text-primary underline underline-offset-4">
            {t('auth_go_login')}
          </Link>
        </p>
      </form>
    </main>
  );
}
